import { useEffect, useMemo, useState } from "react";
import { CheckSquare } from "react-bootstrap-icons";
import { CheckIcon, DocumentTextIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { setAccountSuggestionStatus } from "@api";
import { toast } from "@components/toast/Toast";
import { cn, formatAmount } from "@utils";
import { suggestionActionLabel } from "./suggestionKinds";
import { useSuggestionAction } from "./useSuggestionAction";
import AiSparkleIcon from "../../portal-template/components/SmartNoteModal/AiSparkleIcon";

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? "" : "s"}`;

// The glyph and palette each kind's activity row uses once it happens (Smart Note's ActivityFeed), so a
// step looks like the activity it will become.
const KIND_ICONS = {
  SendPaymentRequest: { Icon: PaperAirplaneIcon, tint: "bg-primary-50 text-primary-600" },
  SendDocuments: { Icon: DocumentTextIcon, tint: "bg-sky-100 text-sky-700" },
  CreateTask: { Icon: CheckSquare, tint: "bg-slate-100 text-slate-700" },
};

const KindIcon = ({ kind, done }) => {
  const { Icon, tint } = done
    ? { Icon: CheckIcon, tint: "bg-green-100 text-green-700" }
    : (KIND_ICONS[kind] ?? KIND_ICONS.CreateTask);
  return (
    <span className={cn("flex size-8 flex-none items-center justify-center rounded-lg", tint)}>
      <Icon aria-hidden="true" className="size-4" />
    </span>
  );
};

const MEMBERS_SHOWN = 8;

// A plan step in the shape the action flows take: one account keeps its customerId and account; a group carries
// every account's id, so Send loads exactly those rows.
const toSuggestion = (step) => {
  const single = step.members.length === 1 ? step.members[0] : null;
  return {
    id: step.key,
    kind: step.kind,
    title: step.title,
    rationale: step.rationale ?? single?.rationale ?? "",
    evidence: step.evidence?.length ? step.evidence : (single?.evidence ?? []),
    templateId: step.templateId,
    invoiceNumbers: step.invoiceNumbers,
    includePods: step.includePods,
    customerId: single?.customerId ?? null,
    customerIds: step.members.map((m) => m.customerId),
    account: single ? { name: single.name, accountCode: single.accountCode, overdue: single.overdue } : null,
  };
};

const figuresLine = (step) => {
  const single = step.members.length === 1 ? step.members[0] : null;
  const who = single ? (single.name ?? single.accountCode) : plural(step.members.length, "account");
  return [who, step.totalOverdue > 0 && `${formatAmount(step.totalOverdue)} overdue`].filter(Boolean).join(" · ");
};

const Evidence = ({ facts }) => (
  <ul className="mt-2 space-y-1 rounded-lg bg-zinc-50 px-3 py-2 text-xs leading-relaxed text-zinc-600 ring-1 ring-inset ring-zinc-100">
    {facts.map((fact) => (
      <li key={fact} className="flex gap-2">
        <span aria-hidden="true" className="mt-[7px] size-1 flex-none rounded-full bg-zinc-400" />
        {fact}
      </li>
    ))}
  </ul>
);

const WhyToggle = ({ open, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    className="ml-1.5 inline-flex items-center gap-0.5 font-medium text-zinc-700 hover:text-zinc-900"
  >
    Why?
    <ChevronDownIcon aria-hidden="true" className={cn("size-3.5 transition-transform", open && "rotate-180")} />
  </button>
);

// One account inside a grouped step: who, how much, and its own reason.
const Member = ({ member }) => {
  const [whyOpen, setWhyOpen] = useState(false);
  return (
    <li className="py-1.5">
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="min-w-0 truncate font-medium text-zinc-800">
          {member.name ?? member.customerId}
          {member.accountCode && <span className="ml-1 font-normal text-zinc-400">{member.accountCode}</span>}
        </span>
        <span className="flex-none tabular-nums text-zinc-600">{formatAmount(member.overdue)}</span>
      </div>
      <p className="mt-0.5 text-xs leading-relaxed text-zinc-500">
        {member.rationale}
        {member.evidence?.length > 0 && <WhyToggle open={whyOpen} onToggle={() => setWhyOpen((o) => !o)} />}
      </p>
      {whyOpen && <Evidence facts={member.evidence} />}
    </li>
  );
};

const Members = ({ members }) => {
  const [all, setAll] = useState(false);
  const shown = all ? members : members.slice(0, MEMBERS_SHOWN);
  return (
    <div className="mt-2 rounded-lg ring-1 ring-inset ring-zinc-100">
      <ul className="max-h-72 divide-y divide-zinc-100 overflow-y-auto px-3">
        {shown.map((m) => (
          <Member key={m.suggestionId} member={m} />
        ))}
      </ul>
      {members.length > MEMBERS_SHOWN && (
        <button
          type="button"
          onClick={() => setAll((a) => !a)}
          className="w-full border-t border-zinc-100 py-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-900"
        >
          {all ? "Show fewer" : `Show all ${members.length.toLocaleString()}`}
        </button>
      )}
    </div>
  );
};

const StepNumber = ({ n }) =>
  n != null && <span className="w-4 flex-none self-center text-right text-xs tabular-nums text-zinc-400">{n}</span>;

const DoneStep = ({ step, outcome, number }) => (
  <li className="flex gap-3 px-4 py-3">
    <StepNumber n={number} />
    <KindIcon done />
    <div className="min-w-0 flex-1">
      <p className="text-sm leading-snug text-zinc-500">{step.title}</p>
      <p className="mt-0.5 text-xs text-green-700">{outcome ?? "Done"}</p>
    </div>
  </li>
);

const OpenStep = ({ step, suggestion, pending, secondaryLabel, onSend, onSecondary, onDismiss, onHover }) => {
  const group = step.members.length > 1;
  const [whyOpen, setWhyOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  return (
    <li
      className="group relative flex gap-3 px-4 py-3.5 transition-colors hover:bg-zinc-50/70"
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
    >
      <KindIcon kind={step.kind} />
      <div className="min-w-0 flex-1">
        <p className="pr-6 text-sm font-medium leading-snug text-zinc-900">{step.title}</p>
        <p className="mt-0.5 text-xs font-medium tabular-nums text-zinc-600">
          {figuresLine(step)}
          {group && (
            <button
              type="button"
              onClick={() => setMembersOpen((o) => !o)}
              aria-expanded={membersOpen}
              className="ml-1.5 inline-flex items-center gap-0.5 font-medium text-zinc-700 hover:text-zinc-900"
            >
              {membersOpen ? "Hide accounts" : "See accounts"}
              <ChevronDownIcon
                aria-hidden="true"
                className={cn("size-3.5 transition-transform", membersOpen && "rotate-180")}
              />
            </button>
          )}
        </p>
        {!group && (
          <>
            <p className="mt-1.5 text-xs leading-relaxed text-zinc-500">
              {suggestion.rationale}
              {suggestion.evidence.length > 0 && <WhyToggle open={whyOpen} onToggle={() => setWhyOpen((o) => !o)} />}
            </p>
            {whyOpen && <Evidence facts={suggestion.evidence} />}
          </>
        )}
        {group && membersOpen && <Members members={step.members} />}
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={onSend}
            disabled={pending}
            className="whitespace-nowrap rounded-md bg-zinc-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-zinc-700 disabled:opacity-60"
          >
            {pending ? "Working…" : suggestionActionLabel(suggestion, group ? step.members.length : null)}
          </button>
          {secondaryLabel && (
            <button
              type="button"
              onClick={onSecondary}
              disabled={pending}
              className="text-xs font-medium text-zinc-500 opacity-0 transition-opacity hover:text-zinc-900 disabled:opacity-60 group-focus-within:opacity-100 group-hover:opacity-100"
            >
              {secondaryLabel}
            </button>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={`Dismiss: ${step.title}`}
        title="Dismiss"
        className="absolute right-3 top-3 rounded-md p-1 text-zinc-400 opacity-0 transition-opacity hover:bg-zinc-100 hover:text-zinc-700 group-focus-within:opacity-100 group-hover:opacity-100"
      >
        <XMarkIcon aria-hidden="true" className="size-3.5" />
      </button>
    </li>
  );
};

// Home's one-line step: number, what to do, who, how much, and the action on the right.
const CompactStep = ({ number, step, suggestion, pending, secondaryLabel, onSend, onSecondary, onDismiss }) => {
  const group = step.members.length > 1;
  const [whyOpen, setWhyOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const toggle = group
    ? { open: membersOpen, set: setMembersOpen, label: membersOpen ? "Hide accounts" : "See accounts" }
    : suggestion.evidence.length > 0 && { open: whyOpen, set: setWhyOpen, label: "Why?" };
  return (
    <li className="group relative px-4 py-3.5 transition-colors first:rounded-t-2xl last:rounded-b-2xl hover:bg-zinc-50/70">
      <div className="flex items-center gap-3">
        <StepNumber n={number} />
        <KindIcon kind={step.kind} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium leading-snug text-zinc-900">{step.title}</p>
          <p className="mt-0.5 truncate text-xs text-zinc-500">
            {group ? plural(step.members.length, "account") : suggestion.rationale}
            {toggle && (
              <button
                type="button"
                onClick={() => toggle.set((o) => !o)}
                aria-expanded={toggle.open}
                className="ml-1.5 inline-flex items-center gap-0.5 font-medium text-zinc-700 hover:text-zinc-900"
              >
                {toggle.label}
                <ChevronDownIcon
                  aria-hidden="true"
                  className={cn("size-3.5 transition-transform", toggle.open && "rotate-180")}
                />
              </button>
            )}
          </p>
        </div>
        {step.totalOverdue > 0 && (
          <span className="flex-none text-sm font-semibold tabular-nums text-zinc-900">
            {formatAmount(step.totalOverdue)}
          </span>
        )}
        <div className="flex flex-none items-center gap-3">
          {secondaryLabel && (
            <button
              type="button"
              onClick={onSecondary}
              disabled={pending}
              className="hidden text-xs font-medium text-zinc-500 hover:text-zinc-900 disabled:opacity-60 group-focus-within:block group-hover:block"
            >
              {secondaryLabel}
            </button>
          )}
          <button
            type="button"
            onClick={onSend}
            disabled={pending}
            className="whitespace-nowrap rounded-md bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-zinc-700 disabled:opacity-60"
          >
            {pending ? "Working…" : suggestionActionLabel(suggestion, group ? step.members.length : null)}
          </button>
        </div>
      </div>
      {(whyOpen || membersOpen) && (
        <div className="ml-[4.75rem] mr-1">
          {whyOpen && <Evidence facts={suggestion.evidence} />}
          {membersOpen && <Members members={step.members} />}
        </div>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label={`Dismiss: ${step.title}`}
        title="Dismiss"
        className="absolute -right-2.5 -top-2.5 rounded-full bg-white p-1 text-zinc-400 opacity-0 shadow-sm ring-1 ring-zinc-200 transition-opacity hover:text-zinc-700 group-focus-within:opacity-100 group-hover:opacity-100"
      >
        <XMarkIcon aria-hidden="true" className="size-3" />
      </button>
    </li>
  );
};

export const PlanSkeleton = () => (
  <ul className="divide-y divide-zinc-100 rounded-xl border border-zinc-200">
    {[0, 1, 2].map((i) => (
      <li key={i} className="flex gap-3 px-4 py-3.5">
        <span className="size-8 flex-none animate-pulse rounded-lg bg-zinc-100" />
        <div className="flex-1 space-y-2 pt-1">
          <div className="h-3 w-3/4 animate-pulse rounded bg-zinc-200" />
          <div className="h-2.5 w-1/3 animate-pulse rounded bg-zinc-100" />
          <div className="h-2.5 w-full animate-pulse rounded bg-zinc-100" />
        </div>
      </li>
    ))}
  </ul>
);

const VARIANTS = {
  panel: {
    pageSize: 10,
    empty: "No suggestions for this set yet — they're prepared overnight.",
    clear: "Nothing needs actioning in this set right now.",
  },
  home: {
    pageSize: 5,
    empty: "No suggestions yet — they're prepared overnight.",
    clear: "Nothing needs actioning right now.",
  },
};

const Heading = ({ home }) =>
  home ? (
    <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500">
      <AiSparkleIcon className="size-3.5" />
      Suggested for today
    </h3>
  ) : (
    <h3 className="text-sm font-semibold text-zinc-900">Plan of action</h3>
  );

/**
 * A working set's plan of action from its accounts' stored suggestions: payment requests with the same template
 * grouped across accounts, tasks and documents one per account, ranked blockers-first then by overdue. Each step
 * is actioned in one click (Send goes to exactly the step's accounts), with a secondary option on hover. Done and
 * Dismissed mark every account in the step. Accounts that moved since their suggestion are hidden until it's
 * regenerated.
 * `onHoverStep(suggestion | null)` lets the host pick out the step's accounts (`customerIds`) while the pointer is
 * over it; `onFlowChange(open)` lets a floating host step back while one of the flows is showing.
 * `variant="home"` is Home's "Suggested for today": one line per numbered step, five at a time.
 */
const PlanOfAction = ({ plan, onHoverStep, onFlowChange, variant = "panel" }) => {
  const home = variant === "home";
  const { pageSize, empty, clear } = VARIANTS[variant];
  const [local, setLocal] = useState(() => new Map());
  const [stepsShown, setStepsShown] = useState(pageSize);

  const steps = useMemo(
    () =>
      (plan?.steps ?? [])
        .map((step) => ({ step, suggestion: toSuggestion(step), mark: local.get(step.key) }))
        .filter(({ mark }) => mark?.status !== "Dismissed"),
    [plan, local],
  );
  const open = steps.filter(({ mark }) => !mark);

  const mark = (suggestion, status, outcome) => {
    const step = plan.steps.find((s) => s.key === suggestion.id);
    if (!step) return;
    onHoverStep?.(null);
    setLocal((prev) => new Map(prev).set(step.key, { status, outcome, amount: step.totalOverdue }));
    setAccountSuggestionStatus({ suggestionIds: step.members.map((m) => m.suggestionId), status }).catch(() => {
      if (status === "Dismissed") toast({ description: "Couldn't dismiss that suggestion." });
    });
  };

  const { send, secondary, secondaryLabel, pending, flowOpen, elements } = useSuggestionAction({
    onDone: (s, outcome) => mark(s, "Done", outcome),
  });
  useEffect(() => onFlowChange?.(flowOpen), [flowOpen, onFlowChange]);
  useEffect(() => () => onHoverStep?.(null), [onHoverStep]);

  const counts = plan?.counts;

  if (steps.length === 0) {
    const nothingYet = !counts || counts.open + counts.done + counts.dismissed + counts.changedSinceGenerated === 0;
    return (
      <section aria-label={home ? "Suggested for today" : "Plan of action"}>
        <div className={home ? "mb-4" : "mb-2.5"}>
          <Heading home={home} />
        </div>
        <p className="rounded-xl border border-dashed border-zinc-200 px-4 py-3 text-xs text-zinc-500">
          {nothingYet ? empty : clear}
        </p>
        {elements}
      </section>
    );
  }

  const done = steps.length - open.length;
  const actioned = steps
    .filter(({ mark }) => mark?.status === "Done")
    .reduce((sum, { mark }) => sum + (mark.amount ?? 0), 0);
  return (
    <section aria-label={home ? "Suggested for today" : "Plan of action"}>
      <div className={cn("flex items-baseline justify-between", home ? "mb-4" : "mb-2.5")}>
        <Heading home={home} />
        <span className={cn("text-xs tabular-nums", home ? "text-zinc-400" : "text-zinc-500")}>
          {open.length === 0 ? "All done" : `${done} of ${steps.length} done`}
          {actioned > 0 && ` · ${formatAmount(actioned)} actioned`}
        </span>
      </div>
      <ol
        className={cn(
          "divide-y divide-zinc-100 rounded-xl border border-zinc-200 bg-white",
          home ? "rounded-2xl" : "overflow-hidden",
        )}
      >
        {steps
          .slice(0, stepsShown)
          .map(({ step, suggestion, mark: marked }, i) =>
            marked ? (
              <DoneStep key={step.key} step={step} outcome={marked.outcome} number={home ? i + 1 : null} />
            ) : home ? (
              <CompactStep
                key={step.key}
                number={i + 1}
                step={step}
                suggestion={suggestion}
                pending={pending === suggestion.id}
                secondaryLabel={secondaryLabel(suggestion)}
                onSend={() => send(suggestion)}
                onSecondary={() => secondary(suggestion)}
                onDismiss={() => mark(suggestion, "Dismissed")}
              />
            ) : (
              <OpenStep
                key={step.key}
                step={step}
                suggestion={suggestion}
                pending={pending === suggestion.id}
                secondaryLabel={secondaryLabel(suggestion)}
                onSend={() => send(suggestion)}
                onSecondary={() => secondary(suggestion)}
                onDismiss={() => mark(suggestion, "Dismissed")}
                onHover={onHoverStep && ((on) => onHoverStep(on ? suggestion : null))}
              />
            ),
          )}
      </ol>
      {steps.length > stepsShown && (
        <button
          type="button"
          onClick={() => setStepsShown((n) => n + pageSize)}
          className="mt-2 w-full rounded-xl border border-zinc-200 bg-white py-2 text-xs font-medium text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
        >
          Show {Math.min(pageSize, steps.length - stepsShown)} more · {(steps.length - stepsShown).toLocaleString()}{" "}
          left
        </button>
      )}
      {elements}
    </section>
  );
};

export default PlanOfAction;
